import { Component } from '@theme/component';
import { throttle, prefersReducedMotion } from '@theme/utilities';

/**
 * Handles animating a single hero media element based on scroll event.
 * 
 * @typedef {object} Refs
 * @property {HTMLElement} heroMotionMedia - The media element to animate
 * 
 * @extends {Component<Refs>}
 */
class MotionMedia extends Component {
  /**
   * Throttled scroll handler to optimize performance
   * @type {(event: Event) => void}
   */
  #throttledScrollHandler;

  /**
   * Intersection observer to track when the section is visible
   * @type {IntersectionObserver}
   */
  #intersectionObserver;

  /**
   * Whether the section is currently visible in the viewport
   * @type {boolean}
   */
  #isVisible = false;

  /**
   * Animation frame ID for cleanup
   * @type {number | null}
   */
  #animationFrame = null;

  /**
   * Initial scroll position when section becomes visible
   * @type {number}
   */
  #initialScrollY = 0;

  /**
   * Whether scrolling has started from the initial position
   * @type {boolean}
   */
  #hasStartedScrolling = false;

  /**
   * Direction multiplier based on data-direction attribute
   * @type {number}
   */
  #direction = 1;

  /**
   * Speed multiplier based on data-speed attribute
   * @type {number}
   */
  #speed = 1;

  /**
   * Configuration for animation behavior
   */
  #config = {
    // Base transform distance in pixels
    baseTransform: 100,
    // Throttle delay for scroll events (in milliseconds)
    throttleDelay: 16, // ~60fps
    // Intersection observer threshold
    threshold: 0.1
  };

  connectedCallback() {
    super.connectedCallback();

    // Don't animate if user prefers reduced motion
    if (prefersReducedMotion()) {
      return;
    }

    this.#parseDataAttributes();
    this.#setupIntersectionObserver();
    this.#throttledScrollHandler = throttle(() => this.#handleScroll(), this.#config.throttleDelay);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
  }

  /**
   * Parses data attributes to configure animation behavior
   */
  #parseDataAttributes() {
    // Parse direction: "left" = -1, "right" = 1 (default)
    const direction = this.dataset.direction || '';
    this.#direction = direction === 'left' ? -1 : 1;

    // Parse speed: defaults to 1, can be any positive number
    const speed = parseFloat(this.dataset.speed || '1') || 1;
    this.#speed = Math.max(0.1, speed); // Ensure minimum speed
  }

  /**
   * Sets up intersection observer to only animate when section is visible
   */
  #setupIntersectionObserver() {
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.#isVisible = entry.isIntersecting;
          
          if (this.#isVisible) {
            // Record initial scroll position
            this.#initialScrollY = window.scrollY;
            this.#hasStartedScrolling = false;
            // Start listening to scroll events when section becomes visible
            window.addEventListener('scroll', this.#throttledScrollHandler, { passive: true });
            // Reset to initial state (no transform)
            this.#resetTransform();
          } else {
            // Stop listening when section is not visible
            window.removeEventListener('scroll', this.#throttledScrollHandler);
            // Reset transforms when not visible
            this.#resetTransform();
            this.#hasStartedScrolling = false;
          }
        });
      },
      {
        threshold: this.#config.threshold,
        rootMargin: '50px 0px'
      }
    );

    this.#intersectionObserver.observe(this);
  }

  /**
   * Handles scroll events and calculates animation progress
   */
  #handleScroll() {
    if (!this.#isVisible) return;

    // Check if user has started scrolling from initial position
    const currentScrollY = window.scrollY;
    if (!this.#hasStartedScrolling && Math.abs(currentScrollY - this.#initialScrollY) > 5) {
      this.#hasStartedScrolling = true;
    }

    // Don't animate until scrolling has started
    if (!this.#hasStartedScrolling) {
      return;
    }

    // Cancel any pending animation frame
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
    }

    // Schedule animation for next frame
    this.#animationFrame = requestAnimationFrame(() => {
      this.#updateTransform();
    });
  }

  /**
   * Updates the transform value based on scroll position
   */
  #updateTransform() {
    const rect = this.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    
    // Calculate how much of the section has been scrolled past
    // 0 = section just entering viewport from bottom
    // 1 = section completely scrolled past (top of section at top of viewport)
    const scrollProgress = Math.max(0, Math.min(1, (windowHeight - rect.top) / (windowHeight + rect.height)));
    
    // Calculate transform value based on direction and speed
    const transformValue = scrollProgress * this.#config.baseTransform * this.#direction * this.#speed;

    // Apply transform to the media element
    if (this.refs.heroMotionMedia) {
      this.refs.heroMotionMedia.style.transform = `translateX(${transformValue}px)`;
    }
  }

  /**
   * Resets transform to initial state
   */
  #resetTransform() {
    if (this.refs.heroMotionMedia) {
      this.refs.heroMotionMedia.style.transform = '';
    }
  }

  /**
   * Cleanup method to remove event listeners and observers
   */
  #cleanup() {
    window.removeEventListener('scroll', this.#throttledScrollHandler);

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }

    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
    }
  }
}

if (!customElements.get('motion-media')) {
  customElements.define('motion-media', MotionMedia);
}
